import React from 'react';
import { Box, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography } from '@mui/material';
import InvButton from '../../../components/InvButton';
import { STOCK_STATUS } from '../../../utils/pharmacyAvailability';
import { IPD_COLORS } from '../../../theme';
import { lineTableSx } from '../pharmacyStyles';

/** Same wording and colours Active Dispenses uses for these three states. */
function AvailabilityCell({ entry }) {
  if (!entry || entry.loading) {
    return <CircularProgress size={14} sx={{ color: IPD_COLORS.primary }} />;
  }
  if (entry.status === STOCK_STATUS.NOT_LISTED) {
    return (
      <Tooltip title="This medicine is not registered in the pharmacy catalogue">
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.textMuted }}>
          Not in catalogue
        </Typography>
      </Tooltip>
    );
  }
  if (entry.status === STOCK_STATUS.OUT_OF_STOCK) {
    return (
      <Tooltip title="Prescribed item is out of stock — choose a substitute if available">
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.warning.text }}>
          Out of stock
        </Typography>
      </Tooltip>
    );
  }
  if (entry.status === STOCK_STATUS.IN_STOCK) {
    const qty = Number(entry.item?.available_quantity ?? entry.item?.stock);
    return (
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.success.text }}>
        In stock{Number.isFinite(qty) && qty > 0 ? ` · ${qty}` : ''}
      </Typography>
    );
  }
  return <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>—</Typography>;
}

/**
 * What the doctor prescribed. OPD prescriptions carry free-text drug names with no
 * item_id, so this is reference material — the pharmacist confirms the real inventory
 * item alongside it.
 *
 * Pass `availability` (keyed by medicine id) and `onSubstitute` to turn it into the
 * working stock-check table used by the dispense flow. Without them it stays the
 * read-only view the prescription dialog renders.
 */
export default function PrescribedMedicinesTable({ medications = [], availability, onSubstitute }) {
  const showStock = Boolean(availability);

  if (!medications.length) {
    return (
      <Typography sx={{ py: 2, textAlign: 'center', color: IPD_COLORS.textSecondary, fontSize: 13 }}>
        No medicines on this prescription.
      </Typography>
    );
  }

  return (
    <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Medicine</TableCell>
            <TableCell>Dose</TableCell>
            <TableCell>Route</TableCell>
            <TableCell>Frequency</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Instructions</TableCell>
            {showStock && <TableCell>Availability</TableCell>}
            {showStock && <TableCell align="right" sx={{ width: 130 }}>Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {medications.map((med) => {
            const entry = showStock ? availability[med.id] : null;
            const resolved = entry?.status === STOCK_STATUS.IN_STOCK;
            return (
              <TableRow key={med.id}>
                <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>{med.name || '—'}</TableCell>
                <TableCell>{med.dose || '—'}</TableCell>
                <TableCell>{med.route || '—'}</TableCell>
                <TableCell>{med.frequency || '—'}</TableCell>
                <TableCell>{med.duration || '—'}</TableCell>
                <TableCell sx={{ minWidth: 160 }}>{med.instructions || '—'}</TableCell>
                {showStock && <TableCell><AvailabilityCell entry={entry} /></TableCell>}
                {showStock && (
                  <TableCell align="right">
                    {entry?.loading || resolved ? (
                      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>—</Typography>
                    ) : (
                      <InvButton size="small" variant="outlined" tone="teal"
                        onClick={() => onSubstitute?.(med)}>
                        Substitute
                      </InvButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
